import { useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ShieldCheck, Smartphone, KeyRound } from "lucide-react";
import logo_raya_EN from "assets/images/landing/logo_raya_EN1.png";
import LoginPage from "./components/SignIn";
import ForgotPassword from "./components/ForgotPassword";
import AnimatedBackground from "units/client/component/AnimatedBackground";
import { useTranslation } from "react-i18next";

/* همان انیمیشن‌ها و استایل پوسته‌ی LoginPage (فقط برای حالت فراموشی رمز لازم است) */
const shellStyles = `
@keyframes raya-spin { to { transform: rotate(360deg); } }
@keyframes raya-spin-rev { to { transform: rotate(-360deg); } }
@keyframes raya-card-in {
  from { opacity: 0; transform: translateY(18px) scale(.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes raya-gradient {
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
.raya-ring-a { animation: raya-spin 22s linear infinite; }
.raya-ring-b { animation: raya-spin-rev 34s linear infinite; }
.raya-card-in { animation: raya-card-in .7s cubic-bezier(.2,.8,.2,1) both; }
.raya-title { background-size: 200% 200%; animation: raya-gradient 8s ease infinite; }
@media (prefers-reduced-motion: reduce) {
  .raya-ring-a, .raya-ring-b,
  .raya-card-in, .raya-title { animation: none !important; }
}
.auth-form input:-webkit-autofill,
.auth-form input:-webkit-autofill:focus {
  -webkit-text-fill-color: #111827;
  caret-color: #111827;
  -webkit-box-shadow: 0 0 0 1000px #fff inset;
}
.dark .auth-form input:-webkit-autofill,
.dark .auth-form input:-webkit-autofill:focus {
  -webkit-text-fill-color: #fff;
  caret-color: #fff;
  -webkit-box-shadow: 0 0 0 1000px #18181b inset;
}
.dark .pw-strength p { color: #99f6e4 !important; }
`;

const Auth = () => {
    const { t } = useTranslation(["user"]);

    const location = useLocation();
    const mode = location?.state?.mode;

    // حالت ورود / ثبت‌نام: LoginPage پوسته‌ی کامل خودش را دارد
    if (mode !== "forgotPassword") {
        return <LoginPage />;
    }

    // حالت فراموشی رمز: همان پوسته‌ی LoginPage دور ForgotPassword
    return (
        <section
            dir="rtl"
            className="relative min-h-screen w-full overflow-hidden flex items-center justify-center px-4 py-8 sm:py-12"
        >
            <style>{shellStyles}</style>
            {/* LoginPage در این حالت mount نیست، پس ToastContainer فقط همین‌جا هست (تکراری نمی‌شود) */}
            <ToastContainer rtl position="bottom-center" containerId="main-toast" newestOnTop autoClose={5000} />

            <AnimatedBackground />

            {/* Glass shell */}
            <div
                className="raya-card-in relative z-10 w-full max-w-5xl grid md:grid-cols-[1.05fr_1fr] overflow-hidden
                           rounded-[1.75rem] sm:rounded-[2rem]
                           border border-white/70 bg-white/55 backdrop-blur-2xl
                           shadow-[0_30px_80px_-24px_rgba(15,46,46,.45)]
                           dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[0_30px_80px_-24px_rgba(0,0,0,.7)]"
            >
                {/* Brand panel */}
                <div
                    className="relative overflow-hidden flex flex-col items-center justify-center text-center
                               px-6 pt-8 pb-7 md:p-12
                               bg-gradient-to-br from-[#1c4747] via-[#17605d] to-[#0d2f2f] text-white"
                >
                    <div aria-hidden="true" className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full
                                                       border border-[#f2c14e]/25 raya-ring-b" />
                    <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -right-20 h-80 w-80 rounded-full
                                                       border border-white/10" />

                    {/* logo with rotating gold ring */}
                    <div className="relative h-[104px] w-[104px] md:h-36 md:w-36 flex items-center justify-center">
                        <div
                            aria-hidden="true"
                            className="raya-ring-a absolute inset-0 rounded-full"
                            style={{
                                background:
                                    "conic-gradient(from 0deg, #f2c14e, rgba(242,193,78,0) 35%, rgba(255,255,255,.7) 55%, rgba(242,193,78,0) 75%, #f2c14e)",
                                WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px))",
                                mask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px))",
                            }}
                        />
                        <div className="h-[86px] w-[86px] md:h-[120px] md:w-[120px] rounded-full bg-white/95 shadow-xl
                                        flex items-center justify-center ring-4 ring-white/10">
                            <img src={logo_raya_EN} alt="Raya" className="object-contain w-[60px] md:w-[84px] block" />
                        </div>
                    </div>

                    <h2
                        className="raya-title mt-5 md:mt-8 text-2xl md:text-[32px] leading-tight font-extrabold
                                   bg-gradient-to-r from-white via-[#ffe7a3] to-white bg-clip-text text-transparent"
                    >
                        {t("authShell.welcomeTitle")}
                    </h2>
                    <p className="mt-2 text-sm md:text-base text-teal-50/80 max-w-xs">
                        {t("authShell.welcomeSubtitle")}
                    </p>

                    <ul className="hidden md:flex flex-col gap-3 mt-10 text-sm text-teal-50/85 text-right">
                        <li className="flex items-center gap-3">
                            <span className="h-9 w-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center">
                                <Smartphone className="w-4 h-4 text-[#f2c14e]" />
                            </span>
                            {t("authShell.features.phoneLogin")}
                        </li>
                        <li className="flex items-center gap-3">
                            <span className="h-9 w-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center">
                                <KeyRound className="w-4 h-4 text-[#f2c14e]" />
                            </span>
                            {t("authShell.features.passwordOrOtp")}
                        </li>
                        <li className="flex items-center gap-3">
                            <span className="h-9 w-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center">
                                <ShieldCheck className="w-4 h-4 text-[#f2c14e]" />
                            </span>
                            {t("authShell.features.secureAccount")}
                        </li>
                    </ul>
                </div>

                {/* Form panel */}
                <div className="p-5 sm:p-8 md:p-12 flex flex-col justify-center">
                    <ForgotPassword />
                </div>
            </div>
        </section>
    );
};

export default Auth;